/**
 * Memory Cache Module
 * Replaces: HttpRuntime.Cache, MemoryCache in CacheMangement.cs
 * Original: CacheMangement.cs storeDtCache(), getDtCache() methods
 * 
 * Provides in-memory LRU cache with TTL support for hot data
 */

export interface CacheEntry<T> {
  value: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

export interface CacheOptions {
  ttl?: number; // Default TTL in milliseconds
  maxSize?: number; // Maximum number of entries
  /**
   * When set, the cache mirrors itself into localStorage under this key so it
   * survives a page/runtime reload instead of starting empty. Opt-in: leave it
   * unset for hot per-request caches that don't need to persist.
   *
   * Same rationale as DataStore's INSYNC_IAD_CACHE_V1 mirror — Office restarts
   * the runtime (page refresh, pane navigation) and takes the in-memory Map with
   * it. localStorage is the store the taskpane runtime keeps across reloads.
   * Values must be JSON-serializable.
   */
  persistKey?: string;
}

/**
 * LRU Cache with TTL
 * Replaces: HttpRuntime.Cache from .NET
 */
export class MemoryCache<T = any> {
  private cache: Map<string, CacheEntry<T>>;
  private options: Required<Omit<CacheOptions, 'persistKey'>>;

  // localStorage persistence (opt-in via options.persistKey). Debounced so a
  // bulk load serializes once after it settles rather than on every set.
  private persistKey: string | null = null;
  private persistEnabled = false;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly PERSIST_DEBOUNCE_MS = 1000;
  private static readonly PERSIST_MAX_BYTES = 4 * 1024 * 1024; // localStorage caps ~5MB/origin

  constructor(options: CacheOptions = {}) {
    this.cache = new Map();
    this.options = {
      ttl: options.ttl || 30 * 60 * 1000, // Default 30 minutes
      maxSize: options.maxSize || 1000 // Default 1000 entries
    };

    if (options.persistKey && typeof localStorage !== 'undefined') {
      this.persistKey = options.persistKey;
      this.persistEnabled = true;
      this.hydrateFromStorage();
      // The debounce may not fire before Office navigates the pane away, so also
      // flush on the way out. Both are best-effort; neither is guaranteed alone.
      if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', () => this.persistNow());
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') this.persistNow();
        });
      }
    }
  }

  /**
   * Rehydrate live (unexpired) entries from the localStorage mirror. Corrupt or
   * unreadable data is dropped so the cache starts cold rather than throwing.
   */
  private hydrateFromStorage(): void {
    if (!this.persistEnabled || !this.persistKey) return;
    try {
      const raw = localStorage.getItem(this.persistKey);
      if (!raw) return;
      const now = Date.now();
      const entries = JSON.parse(raw) as [string, CacheEntry<T>][];
      for (const [key, entry] of entries) {
        // Keep original timestamp/ttl so the TTL window is preserved across
        // reloads — this never revives an already-expired entry.
        if (entry && typeof entry.timestamp === 'number' && now - entry.timestamp <= entry.ttl) {
          this.cache.set(key, entry);
        }
      }
    } catch {
      try { localStorage.removeItem(this.persistKey); } catch { /* best-effort */ }
    }
  }

  /**
   * Write the mirror now. Never throws: a failed mirror only costs a refetch. On
   * quota failure, retry once with the newest half, then give up for this runtime.
   */
  private persistNow(): void {
    if (this.persistTimer !== null) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    if (!this.persistEnabled || !this.persistKey) return;

    const now = Date.now();
    // Live entries only, newest first so a quota trim keeps the freshest.
    let entries = Array.from(this.cache.entries())
      .filter(([, entry]) => now - entry.timestamp <= entry.ttl)
      .sort((a, b) => b[1].timestamp - a[1].timestamp);

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const payload = JSON.stringify(entries);
        if (payload.length > MemoryCache.PERSIST_MAX_BYTES) {
          entries = entries.slice(0, Math.floor(entries.length / 2));
          continue;
        }
        localStorage.setItem(this.persistKey, payload);
        return;
      } catch {
        entries = entries.slice(0, Math.floor(entries.length / 2));
      }
    }

    this.persistEnabled = false;
    try { localStorage.removeItem(this.persistKey); } catch { /* best-effort */ }
  }

  private schedulePersist(): void {
    if (!this.persistEnabled) return;
    if (this.persistTimer !== null) clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => this.persistNow(), MemoryCache.PERSIST_DEBOUNCE_MS);
  }

  /**
   * Store value in cache
   * Equivalent to: HttpRuntime.Cache[cachenameI] = dt;
   * Original: CacheMangement.cs storeDtCache() method
   */
  set(key: string, value: T, ttl?: number): void {
    // Remove oldest entry if at capacity
    if (this.cache.size >= this.options.maxSize && !this.cache.has(key)) {
      this.evictLRU();
    }

    // Remove expired entries periodically
    this.cleanup();

    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      ttl: ttl ?? this.options.ttl
    });

    this.schedulePersist();
  }

  /**
   * Get value from cache
   * Returns null if expired or not found
   * Equivalent to: HttpRuntime.Cache.Get(cachenameI)
   * Original: CacheMangement.cs getDtCache() method
   */
  get(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    // Check if expired
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    // Move to end (LRU - most recently used)
    this.cache.delete(key);
    this.cache.set(key, entry);

    return entry.value;
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Remove specific key
   * Equivalent to: HttpRuntime.Cache.Remove(cachenameI)
   */
  remove(key: string): boolean {
    const deleted = this.cache.delete(key);
    if (deleted) this.schedulePersist();
    return deleted;
  }

  /**
   * Clear all entries
   * Equivalent to: Loop through HttpRuntime.Cache and remove all
   * Original: CacheMangement.cs ClearAllCache() logic
   */
  clear(): void {
    this.cache.clear();
    // Drop the mirror too, or a reload would resurrect what was just cleared
    // (Refresh/logout rely on clear() actually clearing).
    if (this.persistTimer !== null) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    if (this.persistEnabled && this.persistKey) {
      try { localStorage.removeItem(this.persistKey); } catch { /* best-effort */ }
    }
  }

  /**
   * Get all keys
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Get cache size
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Evict least recently used entry
   */
  private evictLRU(): void {
    const firstKey = this.cache.keys().next().value;
    if (firstKey) {
      this.cache.delete(firstKey);
    }
  }

  /**
   * Remove expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    for (const [key, entry] of entries) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }
}

/**
 * Typed cache for specific data types
 */
export class TypedMemoryCache {
  // Cache for DataTables (converted to arrays of objects)
  static dataTableCache = new MemoryCache<any[]>({ ttl: 20 * 60 * 1000 });
  
  // Cache for ticker autocomplete suggestions
  static autocompleteCache = new MemoryCache<{ ticker: string; companyName: string }[]>({
    ttl: 60 * 60 * 1000, // 1 hour
    maxSize: 100
  });

  // Cache for ticker info all (raw API response)
  // Persisted to localStorage so the autocomplete list survives a page/runtime
  // reload instead of refetching the full ticker universe every refresh.
  static tickerInfoAllCache = new MemoryCache<{ TICKER: string[]; COMPANY_NAME: string[] }>({
    // 24h so an entry never expires mid-window; the IST data-window in the cache
    // key (tickerDataEpoch) is what actually invalidates it at 12:10/17:10 IST,
    // giving one fetch per user per window instead of one per hour.
    ttl: 24 * 60 * 60 * 1000,
    maxSize: 100,
    persistKey: 'INSYNC_TICKERINFO_ALL_CACHE_V1'
  });

  // Cache for ticker info
  static tickerInfoCache = new MemoryCache<any>({
    ttl: 30 * 60 * 1000, // 30 minutes
    maxSize: 500
  });
  
  // Cache for line items
  static lineItemCache = new MemoryCache<any[]>({ 
    ttl: 60 * 60 * 1000, // 1 hour
    maxSize: 200
  });
  
  // Cache for fiscal periods
  static fiscalPeriodCache = new MemoryCache<any[]>({ 
    ttl: 30 * 60 * 1000, // 30 minutes
    maxSize: 200
  });

  /**
   * Clear all caches
   * Equivalent to: ClearAllCache() in CacheMangement.cs
   */
  static clearAll(): void {
    this.dataTableCache.clear();
    this.autocompleteCache.clear();
    this.tickerInfoAllCache.clear();
    this.tickerInfoCache.clear();
    this.lineItemCache.clear();
    this.fiscalPeriodCache.clear();
  }
}

// Singleton instance for general use
export const memoryCache = new MemoryCache();

export default memoryCache;
